<template>
  <div class="bg-white dark:bg-slate-900">
    <div class="grid grid-cols-12 gap-4 h-full mx-auto max-w-[1024px] pt-8 pb-32">
      <main
        class="relative col-span-12 lg:col-span-8 min-h-[400px] h-full bg-stone-100 dark:bg-stone-900 rounded-md shadow-md"
      >
        <div class="absolute top-0">
          <AppImageValidation
            :invalid="invalid"
            :invalid-file-count="invalidFileCount"
            :invalid-file-size-indexes="invalidFileSizeIndexes"
            :invalid-file-dimension-indexes="invalidFileDimensionIndexes"
          />
        </div>
        <div v-if="featureStore.results.length > 0 && featureStore.results[0].url">
          <div class="absolute right-2 top-0 flex gap-2 z-10">
            <slot name="main-top"></slot>
            <AppCopyResultImage :url="featureStore.results[0].url" minimal />
            <div
              class="btn btn--sm btn--blue w-full mt-0 min-w-16 opacity-80 hover:opacity-100"
              @click="downloadImage(featureStore.results[0].url)"
            >
              <i class="fa-solid fa-download"></i>
              <span v-if="featureStore.taskOptions.size === 'small'">SD</span>
            </div>
            <div>
              <button
                v-if="!subscriptionStore.isPaidUser"
                @click="subscriptionStore.subscriptionModal.show()"
                class="btn btn--pink w-max"
                aria-label="Download full quality version (requires subscription)"
                title="Get the highest quality version of your image"
              >
                <i class="fa-solid fa-web-awesome"></i>
                <span>Full HD</span>
              </button>
            </div>
          </div>
        </div>

        <div
          v-if="featureStore.processing"
          class="text-primary font-bold text-lg sm:text-xl text-center absolute z-20 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col space-y-2 items-center justify-center"
        >
          <div class="loader" />
          <div id="progressMessage" class="">Please wait a few seconds...</div>
        </div>
        <div id="preview" :class="{ 'opacity-30 blur-lg': featureStore.processing }">
          <slot name="preview" />
        </div>
      </main>

      <aside
        class="z-40 lg:z-10 lg:relative bottom-0 lg:h-full w-full shadow-md rounded-md col-span-12 lg:col-span-4 bg-stone-100 dark:bg-stone-900 px-6"
      >
        <div class="lg:sticky lg:top-24 flex flex-col">
          <fieldset :disabled="featureStore.processing" class="lg:max-h-full overflow-y-scroll mt-4 py-4">
            <slot name="aside"> </slot>
          </fieldset>

          <div class="lg:order-last py-4 lg:pb-4">
            <div v-if="showCost" class="">
              <div class="flex lg:flex-col gap-2 items-start">
                <label class="input-label flex space-x-2 items-center justify-center" for="small_size">
                  <input
                    v-model="featureStore.taskOptions.size"
                    :disabled="featureStore.processing"
                    id="small_size"
                    class="radio"
                    value="small"
                    type="radio"
                  />
                  <span class="font-normal">
                    <span>Small size</span>
                    <span class="hidden lg:inline">: up to {{ costs[0].max_width }}x{{ costs[0].max_height }}</span>
                  </span>
                </label>
                <label class="input-label label-option--pro flex space-x-2 items-center justify-center" for="full_size">
                  <input
                    v-model="featureStore.taskOptions.size"
                    id="full_size"
                    class="radio"
                    value="full"
                    type="radio"
                  />
                  <span class="font-normal">Full size</span>
                </label>
              </div>
            </div>

            <slot name="action" class="">
              <div class="relative mt-12">
                <div class="btn btn--lg btn--primary w-full" :class="{ 'btn--disabled': disabled }" @click="submit">
                  <div v-if="featureStore.processing" class="flex items-center justify-center">
                    <AppSpinner />
                    <span class="">{{ processingText }}</span>
                  </div>
                  <div v-else class="flex items-center justify-center">
                    <i class="fa-solid fa-bolt-lightning"></i>
                    <span>{{ processText }}</span>
                  </div>
                </div>
                <div
                  v-if="showCost && totalCost > 0"
                  class="absolute -top-10 left-0 flex gap-1 items-center justify-center mt-2"
                >
                  <span class="text-sm italic text-">Required credits:</span>
                  <img src="@/images/icons/coin.png" alt="Cost credits" class="w-6" />
                  <span class="text-amber-600 text-xl">{{ totalCost }}</span>
                </div>
              </div>
            </slot>
          </div>
        </div>
      </aside>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue'
  import { useStore } from '$/stores'
  import { useFeatureStore } from '$/stores/feature'
  import { useSubscriptionStore } from '$/stores/subscription'
  import { useSubmit } from '$/composables/useSubmit'
  import { useValidate } from '$/composables/useValidate'
  import { downloadImage, formatBytes } from '$/utils'
  import AppSpinner from '&/AppSpinner.vue'
  import AppImageValidation from '&/AppImageValidation.vue'
  import AppCopyResultImage from '&/AppCopyResultImage.vue'

  const emit = defineEmits(['finished'])

  withDefaults(
    defineProps<{
      showCost?: boolean
      totalCost?: number
    }>(),
    {
      showCost: false,
      totalCost: 0,
    }
  )

  const store = useStore()
  const featureStore = useFeatureStore()
  const subscriptionStore = useSubscriptionStore()
  const { onSubmit } = useSubmit()
  const { invalid, invalidFileCount, invalidFileSizeIndexes, invalidFileDimensionIndexes } = useValidate()
  const imageDimensions = ref({})
  const imageSize = ref('')

  const disabled = computed(() => {
    return invalid.value || store.sending || featureStore.processing || featureStore.disableSubmit
  })
  const processText = computed(() => {
    if (featureStore.images.length > 1) {
      return `${featureStore.feature.action} ${featureStore.images.length} photos`
    } else {
      return featureStore.feature.action
    }
  })
  const processingText = computed(() => {
    if (featureStore.results.length > 0) {
      const percentage = Math.floor((featureStore.results.length / featureStore.images.length) * 100)
      return `Processing... (${percentage}%)`
    } else {
      return 'Processing...'
    }
  })

  const costs = computed(() => {
    return featureStore.featureLimits.filter((i) => i.feature_slug === featureStore.feature.slug)
  })

  function submit() {
    if (disabled.value) return

    onSubmit(() => emit('finished'))
  }

  // Reset imageData when images changed
  watch(
    () => featureStore.images,
    (newImages) => {
      featureStore.imageData = []
      if (newImages.length === 0) return

      newImages.forEach((image) => {
        const img = new Image()
        img.onload = (event) => {
          imageDimensions.value.width = event.target.naturalWidth
          imageDimensions.value.height = event.target.naturalHeight
          imageSize.value = formatBytes(image.size)

          featureStore.imageData.push({
            name: image.name,
            width: imageDimensions.value.width,
            height: imageDimensions.value.height,
          })
        }
        if (image instanceof File || image instanceof Blob) {
          img.src = URL.createObjectURL(image)
        }
      })
    },
    { immediate: true, deep: true }
  )
</script>
